/**
 * DÉSIGNER UN ÉLÉMENT PRÉCIS, SANS ÉCRIRE UN CHEMIN QUI CASSERA.
 *
 * ⚠️ LE SÉLECTEUR EST ANCRÉ SUR LE BLOC NOMMÉ LE PLUS PROCHE, puis descend jusqu'à
 * l'élément visé. Ni l'un ni l'autre extrême ne convenait :
 *
 *   — Le seul nom du bloc (`[data-cfg="footerDetails"]`) ne permet pas de viser un
 *     mot dans un pied de page qui en contient dix.
 *   — Un chemin complet depuis la racine encode toute la structure au-DESSUS du bloc,
 *     qui n'a rien à voir avec ce qu'on cherche à styler. Elle bouge au premier
 *     remaniement, et la règle du client meurt en silence — on a changé `.form` en
 *     flex et déplacé `.fcase` le mois dernier.
 *
 * Ancré, le chemin ne dépend que de ce qui est DANS le bloc. `[data-cfg]` est déjà un
 * contrat de ce dépôt ; ce qui vit dessous est ce que la règle vise vraiment.
 *
 * ⚠️ LE CALCUL NE TOUCHE PAS AU DOM, il travaille sur `Noeud`. Les tests de ce projet
 * tournent sans navigateur (`environment: 'node'`) : mêlées à la marche dans le DOM,
 * ces règles-là — celles où se cachent les erreurs — ne seraient couvertes par rien.
 */

/** Ce qu'on a besoin de savoir d'un élément. `depuisElement` le lit sur le vrai DOM. */
export type CssNode = {
  tag: string;
  id: string;
  classes: readonly string[];
  /** Son `data-cfg`, s'il en porte un : c'est ce qui en fait une ancre. */
  cfg: string | null;
  /** Son `data-page`, s'il en porte un : l'EXEMPLAIRE d'une page multipliable. */
  page: string | null;
  parent: CssNode | null;
  /** Son rang parmi les frères DE MÊME BALISE, 1 s'il est seul. */
  rank: number;
  onlyOfItsType: boolean;
};

/**
 * ⚠️ LES CLASSES D'ÉTAT NE FONT PAS PARTIE DE L'IDENTITÉ D'UN ÉLÉMENT. `.aimed` est
 * posée par le panneau au survol : un sélecteur qui l'embarquerait ne s'appliquerait
 * que pendant qu'on survole la ligne — donc jamais pour le participant.
 */
const STATE_CLASSES = new Set(["aimed", "is-open", "is-active"]);

const useful = (c: string) => c !== "" && !STATE_CLASSES.has(c);

/** `div.f.f--moyen`, `#truc`, ou `input:nth-of-type(2)` — la marque d'un seul nœud. */
function segment(n: CssNode): string {
  if (n.id) return `#${n.id}`;
  const classes = n.classes.filter(useful).slice(0, 3);
  if (classes.length > 0) return `${n.tag}.${classes.join(".")}`;
  // Sans classe ni identifiant, le rang est le seul discriminant disponible.
  return n.onlyOfItsType ? n.tag : `${n.tag}:nth-of-type(${n.rank})`;
}

export type Target = {
  /** Le sélecteur complet, prêt pour la feuille. */
  selector: string;
  /** L'ancre, si le nœud vit sous un bloc nommé. */
  target: string | null;
};

/**
 * ⚠️ ON NE GARDE QUE LES NŒUDS QUI DISENT QUELQUE CHOSE. Recopier chaque niveau
 * intermédiaire — `div > div > div` — allonge le sélecteur sans le préciser, et chacun
 * de ces `div` est une occasion de casser au premier remaniement. Un descendant
 * (l'espace) plutôt qu'un enfant direct (`>`), pour la même raison : intercaler un
 * conteneur ne doit pas suffire à tout perdre.
 */
export function selectorOf(node: CssNode): Target {
  const segments: string[] = [];
  let current: CssNode | null = node;
  let target: string | null = null;
  let page: string | null = null;

  while (current) {
    /* ⚠️ LE SITE EST LA BORNE, ET LA MONTÉE S'Y ARRÊTE. Sans elle, un élément qu'aucun
       bloc nommé n'abrite — tout l'écran de salle, qui n'en portait aucun — recopiait
       le châssis jusqu'à la racine : `.site body.vise #root div.shell … div.site …`.
       Un `.site` AU-DESSUS de `body` ne correspond à rien, et `.vise` n'existe que
       pendant qu'on vise : la règle acceptée ne s'appliquait jamais. */
    if (current.classes.includes("site")) break;
    /* ⚠️ L'ANCRE NE SUFFIT PAS SUR UNE PAGE MULTIPLIABLE, ET LA MONTÉE CONTINUE JUSQU'À
       SON EXEMPLAIRE. Toutes les sections servent le même écran, donc les mêmes blocs :
       `[data-cfg="infosTitle"]` désignait le titre de CHACUNE, et la règle posée sur la
       section 2 changeait aussi la 1 et la 3 (#556). L'exemplaire (`data-page`) borne
       la règle à la page où on l'a posée. Il est la dernière marque : rien au-dessus
       n'est recopié, et lui-même ne l'est que par ce marqueur. */
    if (current.page) {
      page = current.page;
      const anchor = !target && current.cfg ? `[data-cfg="${current.cfg}"]` : "";
      if (anchor) target = current.cfg;
      segments.unshift(`[data-page="${page}"]${anchor}`);
      break;
    }
    if (!target && current.cfg) {
      target = current.cfg;
      segments.unshift(`[data-cfg="${current.cfg}"]`);
    } else if (!target) {
      // Le nœud visé compte toujours ; les ancêtres seulement s'ils portent une marque.
      const marked = current === node || current.id || current.classes.some(useful);
      if (marked) segments.unshift(segment(current));
    }
    current = current.parent;
  }

  /* Aucun bloc nommé ni exemplaire au-dessus : on retombe sur `.site`, qui borne au
     moins la règle au site — le même confinement que `pourInjection` pose de toute
     façon. */
  if (!target && !page) segments.unshift(".site");

  return { selector: segments.join(" "), target };
}

/** Lit un élément du DOM sous la forme que `selecteurDe` attend. */
export function fromElement(el: Element | null): CssNode | null {
  if (!el || el.nodeType !== 1) return null;
  const siblings = el.parentElement
    ? [...el.parentElement.children].filter((c) => c.tagName === el.tagName)
    : [el];
  return {
    tag: el.tagName.toLowerCase(),
    id: el.id ?? "",
    classes: [...el.classList],
    cfg: el.getAttribute("data-cfg"),
    page: el.getAttribute("data-page"),
    parent: fromElement(el.parentElement),
    rank: siblings.indexOf(el) + 1,
    onlyOfItsType: siblings.length <= 1,
  };
}
